import { Form, Formik, FormikErrors, setIn } from 'formik';

import { VesselMessage } from '../../../../types/VesselMessage';
import { VesselUpdateRequestMessage } from '../../../../types/VesselUpdateRequestMessage';
import { Button } from '../../../cdl/Button/Button';
import { DrawerContentLayout } from '../../../cdl/Drawer/DrawerContentLayout';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { toVesselUpdateRequest, VesselUpdateRequestValues } from '../utils/toVesselUpdateRequest';

import { FormikVesselAdminSection } from './components/FormikVesselAdminSection';
import { FormikVesselDetailsSection } from './components/FormikVesselDetailsSection';
import { FormikVesselLubricationChartSection } from './components/FormikVesselLubricationChartSection';
import { FormikVesselNotificationSettingsSection } from './components/FormikVesselNotificationSettingsSection';

interface EditVesselFormProps {
    vessel: VesselMessage;
    onSubmit: (vessel: VesselUpdateRequestMessage) => Promise<void>;
}

export type FormikVesselDetailsState = VesselUpdateRequestValues;

export const EditVesselForm = ({ vessel, onSubmit }: EditVesselFormProps) => {
    const role = useRole();

    const initialValues: FormikVesselDetailsState = {
        name: vessel.name,
        imo: vessel.imo,
        types: vessel.types || [],
        services: vessel.services || [],
        email: vessel.email || '',
        defaultSupplierId: vessel.defaultSupplierId || '',
        defaultContractId: vessel.defaultContractId ?? null,
        invoiceAddress: {
            companyName: vessel.invoiceAddress.companyName || '',
            street: vessel.invoiceAddress.street || '',
            streetNumber: vessel.invoiceAddress.streetNumber || '',
            zipcode: vessel.invoiceAddress.zipcode || '',
            city: vessel.invoiceAddress.city || '',
            country: vessel.invoiceAddress.country || '',
        },
        postalAddress: {
            companyName: vessel.postalAddress?.companyName || '',
            street: vessel.postalAddress?.street || '',
            streetNumber: vessel.postalAddress?.streetNumber || '',
            zipcode: vessel.postalAddress?.zipcode || '',
            city: vessel.postalAddress?.city || '',
            country: vessel.postalAddress?.country || '',
        },
        vatNumber: vessel.invoiceVAT || '',
        invoiceEmailAddress: vessel.invoiceEmailAddress || '',
        mailNotificationSettings: { ...vessel.mailNotificationSettings },
        lubricationChart: {
            file: undefined,
            added: false,
            removed: false,
        },
        active: vessel.active,
        paying: vessel.paying,
        directOrderAllowed: vessel.directOrderAllowed,
    };

    const validate = (values: FormikVesselDetailsState) => {
        let errors: FormikErrors<FormikVesselDetailsState> = {};

        if (!values.name) {
            errors.name = translate('vessel.form.nameRequired');
        }

        if (!values.imo) {
            errors.imo = translate('vessel.form.imoRequired');
        }

        if (!values.invoiceAddress.companyName) {
            errors = setIn(errors, 'invoiceAddress.companyName', translate('vessel.form.companyNameRequired'));
        }

        if (!values.invoiceAddress.country) {
            errors = setIn(errors, 'invoiceAddress.country', translate('vessel.form.countryRequired'));
        }

        if (!values.invoiceAddress.street) {
            errors = setIn(errors, 'invoiceAddress.street', translate('vessel.form.streetRequired'));
        }

        if (!values.invoiceAddress.streetNumber) {
            errors = setIn(errors, 'invoiceAddress.streetNumber', translate('vessel.form.streetNumberRequired'));
        }

        if (!values.invoiceAddress.zipcode) {
            errors = setIn(errors, 'invoiceAddress.zipcode', translate('vessel.form.zipCodeRequired'));
        }

        if (!values.invoiceAddress.city) {
            errors = setIn(errors, 'invoiceAddress.city', translate('vessel.form.cityRequired'));
        }

        return errors;
    };

    const handleSubmit = async (values: FormikVesselDetailsState) => {
        await onSubmit(toVesselUpdateRequest(vessel, values));
    };

    return (
        <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {translate('vessel.edit.updateVessel')}
                            </Button>
                        }
                    >
                        <FormikVesselDetailsSection customerId={vessel.customerId} vesselId={vessel.id} />
                        <FormikVesselNotificationSettingsSection />
                        <FormikVesselLubricationChartSection lubricationChart={vessel.lubricationChart} />
                        {role.isSuperAdmin() ? <FormikVesselAdminSection /> : null}
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
